import React, { FC, useState, memo } from 'react';
import classNames from 'classnames';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import FileUtils from 'application/utils/FileUtils';
import {
  NOTIFICATION_DEFAULT_DELAY,
  NotificationColors,
  useNotificationContext,
} from 'application/providers/NotificationProvider';
import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronRight from '@zvuk-b2b/react-uikit/ui/icons/ChevronRight';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import IconCopy from '@zvuk-b2b/react-uikit/ui/icons/Copy';
import IconDownload from '@zvuk-b2b/react-uikit/ui/icons/Download';

import { TEXTS } from './texts';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';
import { SelectPlayerFlowType } from '../../types';

import styles from '../index.module.scss';

const appLink = 'https://ftp.muzlab.ru/updates/desktop-player/v2/latest/windows-i386/muzlab-desktop-client-3.0.2.exe';

const DownloadDesktopModal: FC<ModalProps[ModalTypes.DESKTOP_PLAYER_DOWNLOAD]> = ({
  openModal,
  replaceModal,
  close,
}) => {
  const { flowType } = useOnboardingContext();
  const { clearCurrentPoint } = useOnboardingActionContext();

  const [isRequirementsExtanded, setIsRequirementsExtanded] = useState(false);
  const { isMobile } = useMedia();
  const notification = useNotificationContext();

  const onClickCopy = () => {
    navigator.clipboard.writeText(appLink);

    notification.showNotification({
      type: NotificationColors.INFO,
      children: TEXTS.LINK_WAS_COPIED,
      delay: NOTIFICATION_DEFAULT_DELAY,
    });
  };

  const onDownloadClick = () => {
    if (!isMobile) {
      FileUtils.downloadExternalFile(appLink);
      notification.showNotification({
        type: NotificationColors.INFO,
        children: TEXTS.APP_WAS_DOWNLOADED,
        delay: NOTIFICATION_DEFAULT_DELAY,
      });
    } else {
      notification.showNotification({
        type: NotificationColors.INFO,
        children: TEXTS.APP_WAS_NOT_DOWNLOADED,
        delay: NOTIFICATION_DEFAULT_DELAY,
      });
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_DESKTOP_DOWNLOAD_CLICKED, {
      [StreamBindFlowParamNameAMP.FROM_MOBILE]: isMobile,
    });
  };

  const onClickNext = () => {
    replaceModal(ModalTypes.DESKTOP_PLAYER_BIND, {});
  };

  const handleCancel = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      className={styles.DesktopPlayerFlowModal}
      contentClassName={styles.DesktopPlayerFlowModalContent}
      title={TEXTS.MODAL_TITLE}
      actionButtons={[
        <Button
          key="button_desktop_player_modal_next"
          elementId="button_desktop_player_modal_next"
          type={ButtonType.PRIMARY}
          onClick={onClickNext}>
          {TEXTS.NEXT_BUTTON}
        </Button>,
        <Button
          key="button_desktop_player_modal_download_cancel"
          elementId="button_desktop_player_modal_download_cancel"
          type={ButtonType.SECONDARY}
          onClick={() => {
            replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <Button
        type={ButtonType.PLAIN}
        className={classNames(styles.DesktopPlayerFlowModalExpandButton, styles.DesktopPlayerFlowModalButton)}
        onClick={() => {
          setIsRequirementsExtanded(!isRequirementsExtanded);
        }}
        elementId="button_desktop_player_modal_expand_button">
        <span>{TEXTS.INSTALLATION_GUIDE_EXPAND_BUTTON}</span>
        <div className={styles.DesktopPlayerFlowModalExpandButtonIcon}>
          {isRequirementsExtanded ? <IconChevronDown /> : <IconChevronRight />}
        </div>
      </Button>

      {isRequirementsExtanded && (
        <ul data-testid="desktop_player_modal_requirements_list">
          <li>{TEXTS.REQUIREMENTS_ITEM_ONE}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_TWO}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_THREE}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_FOUR}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_FIVE}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_SIX}</li>
        </ul>
      )}

      {isMobile && (
        <Button
          type={ButtonType.PLAIN}
          className={classNames(styles.DesktopPlayerFlowModalCopyButton, styles.DesktopPlayerFlowModalButton)}
          onClick={onClickCopy}
          elementId="button_desktop_player_modal_copy_link">
          <div className={styles.DesktopPlayerFlowModalCopyButtonIcon}>
            <IconCopy />
          </div>
          <span>{TEXTS.COPY_APP_LINK}</span>
        </Button>
      )}

      {!isMobile && (
        <div className={styles.container}>
          <Button
            key="button_desktop_player_modal_download_success"
            elementId="button_desktop_player_modal_download_success"
            type={ButtonType.PLAIN}
            className={classNames(styles.DesktopPlayerFlowModalCopyButton, styles.DesktopPlayerFlowModalButton)}
            onClick={onDownloadClick}>
            <div className={styles.DesktopPlayerFlowModalCopyButtonIcon}>
              <IconDownload />
            </div>
            <span>{TEXTS.SUCCESS_BUTTON}</span>
          </Button>
          <IconButton
            key="button_desktop_player_modal_copy_link"
            elementId="button_desktop_player_modal_copy_link"
            onClick={onClickCopy}>
            <IconCopy className={styles.iconButton} />
          </IconButton>
        </div>
      )}
    </ModalScreen>
  );
};

export default memo(DownloadDesktopModal);
